import { useCallback } from "react";
import { logger } from "../../logger";
import type { FocusState, Project } from "../types";

interface UseFocusManagementProps {
  projects: Project[];
  focusState: FocusState;
  setFocusState: React.Dispatch<React.SetStateAction<FocusState>>;
  transitionInProgress: React.MutableRefObject<boolean>;
}

export function useFocusManagement({
  projects,
  focusState,
  setFocusState,
  transitionInProgress,
}: UseFocusManagementProps) {
  /*
   * BROWSER FOCUS SYNCHRONIZATION
   * Syncs browser Tab navigation with React focus state for visual consistency.
   * This bridges the gap between native browser focus and our custom focus system.
   *
   * Triggered on 'focusin' events when user tabs through interface.
   * Updates focusState to match what the browser has focused.
   */
  const handleFocusChange = useCallback(
    (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      logger.debug("Focus change triggered", {
        tagName: target.tagName,
        className: target.className,
      });

      // SKIP FOCUS HANDLING DURING ATOMIC TRANSITIONS
      // Prevents race conditions where focus events trigger before state updates complete
      if (transitionInProgress.current) {
        logger.debug("Skipping focus change - transition in progress");
        return;
      }

      // SKIP FOCUS HANDLING DURING EDIT MODES
      // When in edit modes, focus events should not interfere with the edit state
      if (
        focusState.mode === "editProject" ||
        focusState.mode === "editActionItem"
      ) {
        logger.debug("Skipping focus change - in edit mode", {
          mode: focusState.mode,
        });
        return;
      }

      // SKIP FOCUS HANDLING ON INPUT FIELDS INSIDE EDIT FORMS
      // When focus is on input/textarea/select elements, don't treat it as card focus
      // This prevents programmatic input focusing from interfering with edit modes
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT"
      ) {
        logger.debug("Skipping focus change - focus on form field", {
          tagName: target.tagName,
        });
        return;
      }

      /*
       * DETECT FOCUS ON "ADD NEW ACTION ITEM" CARDS
       * Updates React state to highlight the correct card when tabbed to
       */
      const newActionItemCard = target.closest("[data-new-action-item]");
      if (newActionItemCard) {
        const projectId = parseInt(
          newActionItemCard.getAttribute("data-project-id") || "-1",
        );
        logger.debug("Focus detected on Add New Action Item card", {
          projectId,
        });

        if (projectId === -1) {
          // Focus on global "Add New Action Item" card
          logger.debug("Setting focus state: globalActionItems mode");
          setFocusState((prev) => ({
            ...prev,
            mode: "globalActionItems",
            actionItemIndex: -1,
          }));
        } else {
          // Focus on project-specific "Add New Action Item" card
          const projectIndex = projects.findIndex((p) => p.id === projectId);
          logger.debug("Setting focus state: actionItems mode", {
            projectIndex,
          });
          if (projectIndex !== -1) {
            setFocusState((prev) => ({
              ...prev,
              mode: "actionItems",
              projectIndex,
              actionItemIndex: -1,
              expandedProjectId: projectId,
            }));
          }
        }
        return;
      }

      /*
       * DETECT FOCUS ON "ADD NEW PROJECT" CARD
       * Updates React state for proper visual highlighting
       */
      const newProjectCard = target.closest("[data-new-project]");
      if (newProjectCard) {
        logger.debug("Focus detected on Add New Project card");
        logger.debug("Setting focus state: projects mode");
        setFocusState((prev) => ({
          ...prev,
          mode: "projects",
          projectIndex: -1,
        }));
        return;
      }

      /*
       * DETECT FOCUS ON ACTION ITEM CONTAINERS
       * Syncs focus when user tabs to action item containers or their Edit buttons
       */
      const actionItemElement = target.closest("[data-action-item-index]");
      if (actionItemElement) {
        const actionIndex = parseInt(
          actionItemElement.getAttribute("data-action-item-index") || "0",
        );
        const projectId = parseInt(
          actionItemElement.getAttribute("data-project-id") || "0",
        );

        logger.debug("Focus detected on action item element", {
          actionIndex,
          projectId,
        });

        // Check if this is a global action item
        if (
          actionItemElement.hasAttribute("data-global-action-item-container") ||
          projectId === -1
        ) {
          logger.debug("Setting focus state: globalActionItems mode");
          setFocusState((prev) => ({
            ...prev,
            mode: "globalActionItems",
            actionItemIndex: actionIndex,
          }));
        } else {
          // This is a project action item
          const projectIndex = projects.findIndex((p) => p.id === projectId);
          logger.debug("Setting focus state: actionItems mode", {
            projectIndex,
          });

          if (projectIndex !== -1) {
            setFocusState((prev) => ({
              ...prev,
              mode: "actionItems",
              projectIndex,
              actionItemIndex: actionIndex,
              expandedProjectId: projectId,
            }));
          }
        }
        return;
      }

      /*
       * DETECT FOCUS ON PROJECT CARDS
       * Syncs focus when user tabs to project cards
       */
      const projectCard = target.closest("[data-project-index]");
      if (projectCard) {
        const projectIndex = parseInt(
          projectCard.getAttribute("data-project-index") || "0",
        );

        logger.debug("Focus detected on project card", { projectIndex });
        logger.debug("Setting focus state: projects mode");

        // Update focus to highlight the project that contains this element
        setFocusState((prev) => ({
          ...prev,
          mode: "projects",
          projectIndex,
        }));
        return;
      }
    },
    [projects, focusState.mode, setFocusState, transitionInProgress],
  );

  return {
    handleFocusChange,
  };
}
